import { createI18nStore, createWireI18n } from '@oneappexchange/lwc-wired';
import { DEFAULT_LANGUAGE, IS_DEVELOPMENT, IS_MFE_ACTIVE, IS_PRODUCTION } from '~/modules/config';

const importDebugLanguage = () => import('../../i18n/__generated__/i18n.debug.json');

export const i18nStore = createI18nStore<I18nMessages>({
    // The server injects labels for the user's language in the i18n global object
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: There's some type issue in i18n-wired-i18n package, I'll get it fixed eventually.
    // For now it's OK to silence the error, since it's only a compile time type error.
    bundledI18n: window.i18n.default,
    defaultLanguage: IS_MFE_ACTIVE
        ? DEFAULT_LANGUAGE
        : document.documentElement.getAttribute('lang') || DEFAULT_LANGUAGE,
    i18nModuleLoaders: {
        ...(IS_PRODUCTION ? undefined : { '?': importDebugLanguage, debug: importDebugLanguage }),
        // Add additional languages here
        en: () => import('../../i18n/__generated__/i18n.en.json'),
        es: () => import('../../i18n/__generated__/i18n.es.json'),
        ja: () => import('../../i18n/__generated__/i18n.ja.json'),
    },
    storeName: 'i18nStore',
    // Override the missing text symbol for testing and production into empty string,
    // to ensure dev-only artifacts are not displayed to users if a label is missing.
    missingTextSymbol: IS_DEVELOPMENT ? undefined : '',
    developmentMode: IS_DEVELOPMENT,
});

export type I18nState = {
    isLoading: boolean;
    currentLanguage: string;
    i18n: I18nModule;
    i18nStaticCache: I18nMessages;
};

export const WireI18n = createWireI18n(i18nStore);

//#region WireStore selectors
// Use of this selector requires using 'equalityFn' to avoid unnecesary lwc render calls
export function selectI18n(state: I18nState) {
    return { i18n: state.i18n, i18nStaticCache: state.i18nStaticCache };
}

export function selectI18nModule(state: I18nState = i18nStore.getState()) {
    return state.i18n;
}

export function selectI18nStaticCache(state: I18nState = i18nStore.getState()) {
    return state.i18nStaticCache;
}

export function selectCurrentLanguage(state: I18nState = i18nStore.getState()) {
    return state.currentLanguage;
}
//#endregion WireStore selectors

//#region WireI18n selectors
export function selectSharedLabels(i18nStaticCache: I18nMessages = selectI18nStaticCache()) {
    return i18nStaticCache.shared;
}

export function selectLocationLabels(i18nStaticCache: I18nMessages = selectI18nStaticCache()) {
    return i18nStaticCache.location;
}
// TODO: All selectors down below should be in their respective files, not in the i18nStore
export function selectPrivateListingBanner(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.listingDetails.privateBanner;
}

export function selectCompatibility(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.compatibility;
}

export function selectMoreResourcesSection(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.listingDetails.moreResources;
}

export function selectFilterLocation(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.search.filters.location;
}

export function selectSearchLabels(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.search;
}

export function selectHelpCenter(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.header.helpCenter;
}

export function selectOafLabels(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.oaf;
}

export function selectTypeAhead(i18nStaticCache: I18nMessages) {
    return i18nStaticCache.typeAhead;
}

export type I18nExploreLabels = {
    explore: I18nMessages['explore'];
    search: I18nMessages['search'];
};

export function selectExplorePageLabels(i18nStaticCache: I18nMessages): I18nExploreLabels {
    return { explore: i18nStaticCache.explore, search: i18nStaticCache.search };
}

//#endregion WireI18n selectors
